// Связь с сервером для экранов «не загрузилось» (Алексей 26.10 #2635): вместо красного «Не удалось
// загрузить данные» — «вы не в сети» / «сервер обновляется, подождите» с прогрессом / и только если
// непонятно — «не получилось подключиться». Сюда стекаются сбои из axios-перехватчика; пока сервер
// лежит, опрашиваем его сами и, когда ответил, перезапрашиваем все данные SWR — без «обновите страницу».
//
// Бэкенд при редеплое лежит ~30–40 с (по monitor.log/логам celery-cctv 26.10: RUN → gunicorn слушает
// ≈ 50 с, первые 200 ≈ 56 с; сборка образа ~20 с идёт ДО остановки). Сам он о деплое рассказать не может —
// статус берём с фронтового nginx: /deploy-status.json, его пишет ops/deploy_monitor.sh. Нет файла
// (nginx отдаёт index.html — SPA-фолбэк) → оцениваем по времени, с которого сервер не отвечает.
import { useSyncExternalStore } from 'react';
import { mutate } from 'swr';
import { API_BASE } from '@/api/base';

export type DeployStatus = {
  state: 'deploying' | 'done' | 'failed';
  target?: string;
  /** ISO, начало сборки (RUN в deploy_monitor). */
  started_at: string;
  /** ISO, когда монитор закончил (compose up вернулся); сервер после этого ещё стартует. */
  finished_at?: string;
  expected_seconds?: number;
};

/** После «done» gunicorn поднимается ещё ~50 с — столько продолжаем говорить «обновляется» (Алексей 03.11 #3541:
 *  «"сервер обновляется" на моих глазах превратилось в "сервер не отвечает"»). */
export const DEPLOY_STARTUP_GRACE_MS = 3 * 60_000;
/** Идёт ли деплой с точки зрения экрана: сборка или старт сервера сразу после неё. */
export function deployActive(d: DeployStatus | null | undefined, now: number): d is DeployStatus {
  if (!d) return false;
  if (d.state === 'deploying') return now - Date.parse(d.started_at) < DEPLOY_UNKNOWN_AFTER_MS;
  if (d.state !== 'done') return false;
  const end = d.finished_at ? Date.parse(d.finished_at) : Date.parse(d.started_at) + (d.expected_seconds ?? 90) * 1000;
  return now - end < DEPLOY_STARTUP_GRACE_MS;
}

export type ServerHealth =
  | { kind: 'ok' }
  /** Был сетевой сбой/5xx — проверяем, лежит ли сервер. */
  | { kind: 'checking'; since: number }
  | { kind: 'offline'; since: number }
  | { kind: 'down'; since: number; deploy: DeployStatus | null }
  /** Сервер снова ответил: робот улетает, потом перезапрос данных. */
  | { kind: 'back'; since: number; deploy: DeployStatus | null };

/** Сколько ждём «обновления» без файла статуса, прежде чем честно сказать «непонятно». */
export const DOWN_UNKNOWN_AFTER_MS = 5 * 60_000;
/** Деплой, про который сказал монитор, но затянувшийся сверх меры — тоже «непонятно». */
export const DEPLOY_UNKNOWN_AFTER_MS = 15 * 60_000;
/** Без файла статуса: типичное окно, пока сервер не отвечает при редеплое. */
export const FALLBACK_DOWN_MS = 45_000;
/** С файлом: от начала сборки до первого ответа (медиана ~55 с, p90 ~75 с). */
export const DEFAULT_DEPLOY_MS = 75_000;
/** Сколько длится «снова на связи» — ровно чтобы робот успел улететь. */
export const BACK_MS = 1400;
const POLL_MS = 2500;

let state: ServerHealth = { kind: 'ok' };
const listeners = new Set<() => void>();
const set = (s: ServerHealth) => {
  if (s.kind === 'offline' || s.kind === 'down') wasOut = true;
  state = s;
  listeners.forEach((l) => l());
};

let pollTimer: number | null = null;
let probing = false;
// Были офлайн/сервер лежал — по возвращении перезапросить данные (а не просто погасить «проверяем»).
let wasOut = false;

const isOffline = () => typeof navigator !== 'undefined' && navigator.onLine === false;

async function fetchDeployStatus(): Promise<DeployStatus | null> {
  // Без таймаута зависший запрос (фронт как раз пересоздаётся) держал probing=true навсегда: опрос вставал,
  // экран «не отвечает» не уходил до ручного обновления (Алексей 03.11 #3543).
  const ctrl = new AbortController();
  const t = window.setTimeout(() => ctrl.abort(), 5000);
  try {
    const r = await fetch('/deploy-status.json', { cache: 'no-store', signal: ctrl.signal });
    if (!r.ok) return null;
    // Файла нет → SPA-фолбэк nginx отдаёт index.html с кодом 200: это не статус.
    if (!(r.headers.get('content-type') ?? '').includes('json')) return null;
    const j = (await r.json()) as DeployStatus;
    return j && typeof j.started_at === 'string' ? j : null;
  } catch {
    return null;
  } finally {
    window.clearTimeout(t);
  }
}

/** Жив ли API: любой ответ Django (401 тоже) — жив; traefik без контейнера отвечает без CORS → TypeError. */
async function apiAlive(): Promise<boolean> {
  const ctrl = new AbortController();
  const t = window.setTimeout(() => ctrl.abort(), 8000);
  try {
    const r = await fetch(`${API_BASE}/core/api/auth/users/me/`, { cache: 'no-store', signal: ctrl.signal });
    return r.status < 500;
  } catch {
    return false;
  } finally {
    window.clearTimeout(t);
  }
}

function schedule(ms = POLL_MS) {
  if (pollTimer != null) window.clearTimeout(pollTimer);
  pollTimer = window.setTimeout(() => { pollTimer = null; void probe(); }, ms);
}

function recovered() {
  const prev = state;
  if (prev.kind === 'ok' || prev.kind === 'back') return;
  if (prev.kind === 'checking' && !wasOut) { set({ kind: 'ok' }); return; }
  wasOut = false;
  set({ kind: 'back', since: Date.now(), deploy: prev.kind === 'down' ? prev.deploy : null });
  window.setTimeout(() => {
    if (state.kind !== 'back') return;
    set({ kind: 'ok' });
    // Перезапросить всё, что есть в кэше SWR: страницы с ошибкой сами покажут данные.
    void mutate(() => true);
  }, BACK_MS);
}

async function probe() {
  if (probing) return;
  if (isOffline()) {
    if (state.kind !== 'offline') set({ kind: 'offline', since: Date.now() });
    return; // ждём события online
  }
  probing = true;
  try {
    const [alive, deploy] = await Promise.all([apiAlive(), fetchDeployStatus()]);
    if (alive) { recovered(); return; }
    if (isOffline()) { set({ kind: 'offline', since: Date.now() }); return; }
    const since = state.kind === 'down' || state.kind === 'checking' ? state.since : Date.now();
    set({ kind: 'down', since, deploy });
    schedule();
  } finally {
    probing = false;
  }
}

const isSuspect = (status: number | undefined) => status == null || status === 502 || status === 503 || status === 504;

// ── Сбои отдельных запросов (Алексей 29.10 #3224) ─────────────────────────────────────────────
// Сервер может быть жив (проба /users/me отвечает), но тормозить: часть GET падает по таймауту/5xx.
// Раньше каждая секция писала свой текст («Не удалось…» по пять раз на странице), а общий статус
// считал «всё ок». Теперь сбои GET собираются здесь, одна плашка в каркасе говорит, что не всё
// загрузилось, и сама перезапрашивает данные с нарастающей паузой; секции показывают тихую заглушку.
// 4xx не считаем: 404/403 — это «нет функции/доступа», их страница объясняет сама.
const failed = new Map<string, number>(); // url → когда упал
let failSeq = 0;
let lastFailAt = 0;
let autoRetryTimer: number | null = null;
let autoRetryStep = 0;
const AUTO_RETRY_MS = [10_000, 20_000, 40_000, 60_000];

export type FailureState = { count: number; since: number; lastAt: number; seq: number };
let failureState: FailureState = { count: 0, since: 0, lastAt: 0, seq: 0 };
const failureListeners = new Set<() => void>();
function publishFailures() {
  const since = failed.size ? Math.min(...failed.values()) : 0;
  failureState = { count: failed.size, since, lastAt: lastFailAt, seq: failSeq };
  failureListeners.forEach((l) => l());
  if (failed.size && autoRetryTimer == null && state.kind === 'ok') {
    const ms = AUTO_RETRY_MS[Math.min(autoRetryStep, AUTO_RETRY_MS.length - 1)];
    autoRetryTimer = window.setTimeout(() => {
      autoRetryTimer = null;
      autoRetryStep += 1;
      if (failed.size && state.kind === 'ok') void mutate(() => true);
    }, ms);
  }
  if (!failed.size) {
    autoRetryStep = 0;
    if (autoRetryTimer != null) { window.clearTimeout(autoRetryTimer); autoRetryTimer = null; }
  }
}

const keyOf = (url: string | undefined) => (url ?? '').split('#')[0];
const isServerFault = (status: number | undefined) => status == null || status >= 500;

/** Из перехватчика axios: GET упал без ответа или с 5xx. */
export function reportGetFailure(url: string | undefined, status: number | undefined) {
  if (!url || !isServerFault(status)) return;
  const k = keyOf(url);
  if (!failed.has(k)) failed.set(k, Date.now());
  failSeq += 1;
  lastFailAt = Date.now();
  publishFailures();
}

/** Из перехватчика axios: GET ответил — снять его из «не загрузилось». */
export function reportGetOk(url: string | undefined) {
  if (!url || !failed.size) return;
  if (failed.delete(keyOf(url))) publishFailures();
}

/** Ушли со страницы — сбои прошлой страницы больше не наши. */
export function clearFailures() {
  if (!failed.size) return;
  failed.clear();
  publishFailures();
}

const subscribeFailures = (l: () => void) => { failureListeners.add(l); return () => { failureListeners.delete(l); }; };
const failureSnapshot = () => failureState;
export function useRequestFailures(): FailureState {
  return useSyncExternalStore(subscribeFailures, failureSnapshot, failureSnapshot);
}

/** Плашка в каркасе сейчас видна → секции не повторяют текст, а показывают тихую заглушку. */
export function useGlobalNoticeActive(): boolean {
  const h = useServerHealth();
  const f = useRequestFailures();
  return h.kind !== 'ok' || f.count > 0;
}

/** Из перехватчика axios: запрос упал без ответа или с 502/503/504 — проверить, жив ли сервер. */
export function reportRequestFailure(status: number | undefined) {
  if (!isSuspect(status)) return;
  if (isOffline()) { if (state.kind !== 'offline') set({ kind: 'offline', since: Date.now() }); return; }
  if (state.kind === 'ok') { set({ kind: 'checking', since: Date.now() }); void probe(); }
}

/** Из перехватчика axios: пришёл нормальный ответ — сервер жив. */
export function reportRequestOk() {
  // Из «лежит» выводит только своя проба (раз в 2,5 с): при тормозящей базе часть запросов проходит,
  // часть нет, и каждый удачный ответ гонял плашку «лежит → снова на связи → проверяем → лежит»
  // (Алексей 29.10 #3224 «дёргается постоянно»).
  if (state.kind === 'checking' || state.kind === 'offline') recovered();
}

/** Кнопка «Повторить» на экране: сразу проверить, не дожидаясь следующего опроса. */
export function retryNow() {
  if (state.kind === 'ok') { autoRetryStep = 0; void mutate(() => true); return; }
  void probe();
}

if (typeof window !== 'undefined') {
  window.addEventListener('offline', () => set({ kind: 'offline', since: Date.now() }));
  window.addEventListener('online', () => {
    if (state.kind === 'offline') { set({ kind: 'checking', since: Date.now() }); void probe(); }
  });
}

const subscribe = (l: () => void) => { listeners.add(l); return () => { listeners.delete(l); }; };
const snapshot = () => state;

export function useServerHealth(): ServerHealth {
  return useSyncExternalStore(subscribe, snapshot, snapshot);
}

/** Сетевая ли это ошибка/5xx шлюза — то, что может значить «сервер обновляется». */
export function isConnectivityError(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false;
  const e = err as { isAxiosError?: boolean; response?: { status?: number }; name?: string };
  if (e.isAxiosError) return isSuspect(e.response?.status);
  return e.name === 'TypeError'; // fetch без ответа
}
